import { Trans } from '@lingui/macro'
import GpsFixedIcon from '@mui/icons-material/GpsFixed'
import WifiOffIcon from '@mui/icons-material/WifiOff'
import { Box, Typography, useTheme } from '@mui/material'

/**
 * Schermata al posto della mappa quando non c’è rete: messaggio chiaro e invito a usare il GPS per gli incidenti.
 */
export default function OfflineHomeMapPlaceholder({ isMobile, isFormOpen, isHistoryOpen }) {
  const theme = useTheme()
  const barHeight = theme.mixins.toolbar.minHeight
  const formDrawerWidth = theme.mixins.formDrawerWidth

  return (
    <Box
      height={isMobile ? `calc(100vh - ${barHeight * 2}px)` : `calc(100vh - ${barHeight}px)`}
      sx={{
        ...(!isMobile && isFormOpen && { marginInlineStart: `${formDrawerWidth}px` }),
        ...(!isMobile && isHistoryOpen && { marginInlineEnd: `${formDrawerWidth}px` }),
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background:
          theme.palette.mode === 'dark'
            ? 'linear-gradient(145deg, #0f172a 0%, #1e1b4b 45%, #312e81 100%)'
            : 'linear-gradient(145deg, #eef2ff 0%, #e0e7ff 40%, #c7d2fe 100%)',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: theme.palette.mode === 'dark' ? 0.12 : 0.35,
          backgroundImage:
            'radial-gradient(circle at 20% 30%, rgba(99, 102, 241, 0.45) 0%, transparent 45%), radial-gradient(circle at 80% 70%, rgba(34, 197, 94, 0.2) 0%, transparent 40%)',
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          maxWidth: 440,
          mx: 3,
          px: { xs: 3, sm: 4 },
          py: { xs: 4, sm: 5 },
          borderRadius: 4,
          textAlign: 'center',
          backgroundColor:
            theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.75)' : 'rgba(255, 255, 255, 0.88)',
          backdropFilter: 'blur(12px)',
          border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(99, 102, 241, 0.25)'}`,
          boxShadow:
            theme.palette.mode === 'dark'
              ? '0 24px 48px rgba(0,0,0,0.45)'
              : '0 20px 40px rgba(79, 70, 229, 0.12), 0 0 0 1px rgba(255,255,255,0.6) inset',
        }}
      >
        <Box
          sx={{
            width: 72,
            height: 72,
            mx: 'auto',
            mb: 2,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background:
              theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, rgba(99,102,241,0.35), rgba(129,140,248,0.15))'
                : 'linear-gradient(135deg, #e0e7ff, #c7d2fe)',
            border: `2px solid ${theme.palette.mode === 'dark' ? 'rgba(165,180,252,0.5)' : 'rgba(99, 102, 241, 0.35)'}`,
          }}
        >
          <WifiOffIcon
            sx={{
              fontSize: 36,
              color: theme.palette.mode === 'dark' ? '#a5b4fc' : theme.palette.primary.main,
            }}
          />
        </Box>
        <Typography
          variant="h5"
          component="h2"
          sx={{
            fontWeight: 700,
            letterSpacing: '-0.02em',
            mb: 1.5,
            color: theme.palette.mode === 'dark' ? '#f8fafc' : '#1e1b4b',
          }}
        >
          <Trans>You’re offline</Trans>
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: theme.palette.mode === 'dark' ? 'rgba(226,232,240,0.92)' : 'rgba(30, 27, 75, 0.85)',
            lineHeight: 1.65,
            mb: 2,
          }}
        >
          <Trans>
            The map is not available without a connection. Use the button in the menu to add a new crash — your
            device GPS position is captured when you start the report.
          </Trans>
        </Typography>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1,
            borderRadius: 999,
            backgroundColor:
              theme.palette.mode === 'dark' ? 'rgba(34, 197, 94, 0.12)' : 'rgba(34, 197, 94, 0.1)',
            border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(74, 222, 128, 0.35)' : 'rgba(34, 197, 94, 0.25)'}`,
          }}
        >
          <GpsFixedIcon sx={{ fontSize: 20, color: theme.palette.success.main }} />
          <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.success.dark }}>
            <Trans>GPS is used when you add a new crash (offline)</Trans>
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}
